import EventFilterActions from 'actions/EventFilterActions';
import { type NavGroupId } from 'components/NavigationFrame/navigation/navGroups';
import React, { useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/** Count or status pill rendered beside a nav item label. */
export interface NavTabBadge {
  label: string;
  tone?: 'neutral' | 'accent' | 'critical';
}

export interface NavTabDescriptor {
  /** True if this item should be shown in the nav bar, false to hide it. */
  show: boolean;

  /** Label to display under the icon. */
  label: string;

  /**
   * Automation testid for the nav item. Fixed rather than built from `label`, which is translated
   * and can be renamed, so it stays the same across locales and copy changes.
   */
  testId: string;

  /** The icon to show */
  icon: { className: string } | { node: React.ReactNode };

  /** A unique ID for this nav item. */
  page: string;

  /**
   * The URL for this menu item.
   *
   * This is used to detect if the item should show as "active".
   * e.g. if this is "/video" and the current URL is "/video/list" then
   * this tab will be highlighted.
   */
  route?: string;

  /**
   * The link to actually navigate to when a user clicks on this menu item.
   * This can be different than the route (e.g. "/video" and "/video/list").
   *
   * If not specified, defaults to `route`.  If `href` is specified, and route is
   * not, then this will render a link that opens in a new tab.
   */
  href?: string;

  /**
   * A function to call when a user clicks on this item.
   * Note that this will not be called if the user right clicks a menu
   * item and picks "Open in new tab".
   */
  onClick?: () => void;

  /**
   * Menu item will only be available to test users with the `showExperimentalFeatures`
   * preference.
   */
  experimental?: boolean;

  /**
   * Menu item will show with a "beta" badge.
   */
  beta?: boolean;

  /**
   * Which section of the sidebar this item belongs to. Ignored by the
   * legacy flat nav; defaults to the unlabelled first section.
   */
  group?: NavGroupId;

  /** Optional count or status pill, e.g. unacknowledged alarms. */
  badge?: NavTabBadge;
}

export interface NavTabProps {
  tab: NavTabDescriptor;
  onSelected: (tab: NavTabDescriptor) => void;
}

/**
 * A "tab" in the left-hand DesktopNav component.
 */
const NavTab: React.FC<NavTabProps> = (props) => {
  const { tab, onSelected } = props;
  const href = tab.href || tab.route;
  const route = tab.route || tab.href;
  const location = useLocation();
  const onClick = useCallback(() => {
    if (tab.onClick) {
      tab.onClick();
    }
    onSelected(tab);
    EventFilterActions.resetFilters();
  }, [tab, onSelected]);

  const isActive =
    route && (location.pathname === route || location.pathname.startsWith(`${route}/`));

  if (tab.href && !tab.route) {
    return (
      <li>
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
          <NavTabButton {...props} />
        </a>
      </li>
    );
  } else if (href) {
    // NavLink will automatically add the "selected" class if the current
    // route matches the `to`, but since we want to check against the `route`,
    // we need to add our own `isActive` prop here.
    return (
      <li>
        <NavLink to={href} onClick={onClick} className={isActive ? 'selected' : ''}>
          <NavTabButton {...props} />
        </NavLink>
      </li>
    );
  }
  return (
    <li>
      <button onClick={onClick}>
        {' '}
        <NavTabButton {...props} />
      </button>
    </li>
  );
};

/**
 * Helper function to render the middle part of a NavTab.
 */
const NavTabButton = (props: NavTabProps) => {
  const { tab } = props;

  const icon = 'node' in tab.icon ? tab.icon.node : <i className={`zmdi ${tab.icon.className}`} />;
  return (
    <div
      className="tab"
      data-intercom-target={`desk-nav-${tab.label.toLocaleLowerCase()}`}
      data-testid={tab.testId}
    >
      {icon}
      <div className="label">{tab.label}</div>
      {tab.beta && <div className="beta-tag">Beta</div>}
    </div>
  );
};

export default NavTab;
